import type { DigestFrequency } from "../schemas/tenant.schema.js";
import type { IndustryType } from "../schemas/industry.schema.js";

/** User type discriminator — users.user_type. */
export type UserType =
  | "provider_admin"
  | "provider_user"
  | "client_admin"
  | "client_user";

/**
 * Portal (client-side) user — operates under /portal and is scoped to its own
 * client company. (personal_user was removed — GAP-017 Option B; a B2C subscriber
 * is now a self-registered client_admin, distinguished by client_company.is_self_registered.)
 */
export function isClientType(ut: UserType): boolean {
  return ut === "client_admin" || ut === "client_user";
}

/**
 * Client user who manages their OWN company (subscriptions, billing, dashboard) —
 * client_admin. "Team management off" for self-service subscribers is now gated by
 * client_company.is_self_registered, not the user type (GAP-017 Option B).
 */
export function isClientManagerType(ut: UserType): boolean {
  return ut === "client_admin";
}

/** Permission flags from provider_roles, resolved per request (06-security.md §4.2). */
export interface Permissions {
  canManageClients: boolean;
  canManageSubscriptions: boolean;
  canManageTeamAssignments: boolean;
  canManageInvoices: boolean;
  canViewFinancials: boolean;
  canManageCatalogue: boolean;
  /** Ratings & Reviews (§6.1) — gates review reply + flag/dispute resolution. */
  canManageReviews: boolean;
  isReadOnly: boolean;
}

/** Output of tenant.getConfig (04-api-design.md §5.1). */
export interface TenantConfig {
  id: string;
  slug: string;
  companyName: string;
  /** Provider's industry — set once at provisioning, immutable (ENH-003). */
  industryType: IndustryType;
  platformDisplayName: string;
  primaryCurrency: string;
  brandPrimaryColour: string;
  brandSecondaryColour: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  emailSenderName: string;
  emailSenderAddress: string;
  notificationDigest: DigestFrequency;
}

/** Public brand payload served by GET /api/brand (04-api-design.md §6.1). */
export interface BrandConfig {
  platformDisplayName: string;
  brandPrimaryColour: string;
  brandSecondaryColour: string;
  logoUrl: string | null;
  faviconUrl: string | null;
}

/** The signed-in user resolved for the ACTIVE tenant (ENH-004 auth.me). */
export interface CurrentUser {
  id: string;
  userType: UserType;
  clientCompanyId: string | null;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * Which app(s) an identity may use (host-as-trust-boundary, AUTH-06/08, GAP-025).
 * Resolved server-side across ALL the identity's memberships, not the active org:
 *   provider role anywhere → 'both'; a self-registered client / platform row → b2c;
 *   a non-self-registered client → b2b.
 */
export type AppAudience = "b2b" | "b2c" | "both";

/**
 * The acting user's effective permission flags, resolved from their v2 provider role
 * (falling back to the v1-derived set while a role is unassigned). Surfaced on `me`
 * so the shell can hide menu entries the user would be rejected from — the SERVER
 * remains the enforcement boundary (doc 23-billing §12).
 */
export interface UserPermissions {
  canManageClients: boolean;
  canManageSubscriptions: boolean;
  canManageTeamAssignments: boolean;
  canManageInvoices: boolean;
  canViewFinancials: boolean;
  canManageCatalogue: boolean;
  canManageReviews: boolean;
  isReadOnly: boolean;
}

/** auth.me — active-tenant user + that tenant's brand (drives routing + branding). */
export interface MeResponse {
  user: CurrentUser;
  tenant: { tenantId: string; slug: string; brand: BrandConfig };
  /** App(s) this identity is allowed on, for the host guard (GAP-025). */
  appAudience: AppAudience;
  /** Null for client-side users, who have no provider-role flags. */
  permissions: UserPermissions | null;
}

/** One provider this identity belongs to — powers the org switcher (ENH-004). */
export interface OrganizationSummary {
  tenantId: string;
  clerkOrgId: string;
  slug: string;
  displayName: string;
  userType: UserType;
  logoUrl: string | null;
  brandPrimaryColour: string;
}
